import {
  RestaurantInquiryItem,
  RestaurantInquiryItemStatus,
  useUpdateRestaurantInquiryItemStatusApi,
} from 'apis/restaurant-inquiries';
import {
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useToast,
} from 'design-system';
import { apiErrorMessage } from 'pages/inquiry-processor/utils/inquiry-actions';
import { useEffect, useState } from 'react';

interface IDeclineAppRequestModal {
  inquiryId: number;
  item: RestaurantInquiryItem | null;
  itemName: string;
  onClose: () => void;
}

/**
 * Ops' "we can't supply this": the item goes UNFULFILLED and the BE cancels the price request in
 * the same transaction, so the restaurant sees it as cancelled. The reason is mandatory (BE-enforced
 * for app items) and lands on the inquiry event + the order log — never shown to the restaurant.
 */
export const DeclineAppRequestModal = ({ inquiryId, item, itemName, onClose }: IDeclineAppRequestModal) => {
  const toast = useToast();
  const updateItemStatus = useUpdateRestaurantInquiryItemStatusApi();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (item) setReason('');
  }, [item]);

  const submit = async () => {
    if (!item || !reason.trim()) return;
    try {
      await updateItemStatus.mutateAsync({
        id: inquiryId,
        itemId: item.id,
        status: RestaurantInquiryItemStatus.UNFULFILLED,
        reason: reason.trim(),
      });
      toast({ title: `Declined ${itemName}`, status: 'success' });
      onClose();
    } catch (err) {
      toast({ title: 'Failed to decline the request', description: apiErrorMessage(err), status: 'error' });
    }
  };

  return (
    <Modal isOpen={!!item} onClose={onClose} size={{ base: 'full', md: 'md' }}>
      <ModalOverlay bg='rgba(15,22,38,.55)' />
      <ModalContent rounded='12px'>
        <ModalHeader>Decline {itemName}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize='sm' color='orange.600' mb='3'>
            The restaurant will see this request as cancelled in the app. This cannot be undone.
          </Text>
          <FormControl isRequired>
            <FormLabel fontSize='sm'>Reason</FormLabel>
            <Textarea
              rows={3}
              maxLength={255}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder='e.g. Out of stock at every supplier this week'
              autoFocus
            />
          </FormControl>
        </ModalBody>
        <ModalFooter gap='2'>
          <Button variant='outline' minH='42px' onClick={onClose} isDisabled={updateItemStatus.isLoading}>
            Keep request
          </Button>
          <Button
            colorScheme='red'
            minH='42px'
            onClick={submit}
            isDisabled={!reason.trim()}
            isLoading={updateItemStatus.isLoading}>
            Decline request
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
